import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/PasswordInput';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/context/AuthContext';

/**
 * ForgotPasswordDialog — 2-step OTP reset flow.
 *   Step 1: user enters email or 10-digit mobile → we call /api/auth/forgot-password/request
 *           and (if the account exists) an OTP is texted to their registered mobile.
 *   Step 2: user enters OTP + new password → /api/auth/forgot-password/verify
 *
 * Works for BOTH customer and staff accounts (backend does not care).
 */
export function ForgotPasswordDialog({ open, onClose }) {
    const [step, setStep] = useState('request');   // 'request' | 'verify' | 'done'
    const [identifier, setIdentifier] = useState('');
    const [otp, setOtp] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [masked, setMasked] = useState({});
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setStep('request'); setIdentifier(''); setOtp('');
        setNewPw(''); setConfirmPw(''); setMasked({}); setBusy(false);
    };
    const close = () => { onClose(); setTimeout(reset, 200); };

    const requestOtp = async () => {
        const val = identifier.trim();
        if (!val || (val.includes('@') ? val.length < 5 : val.replace(/\D/g, '').length !== 10)) {
            return toast.error('Enter your registered email or 10-digit mobile');
        }
        setBusy(true);
        try {
            const res = await axios.post(`${API}/auth/forgot-password/request`, { email_or_mobile: val });
            setMasked({ mobile: res.data.masked_mobile, email: res.data.masked_email });
            setStep('verify');
            toast.success(res.data.message || 'OTP sent');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not send OTP');
        } finally { setBusy(false); }
    };

    const verifyOtp = async () => {
        if (!otp || otp.length < 4) return toast.error('Enter the 6-digit OTP');
        if (!newPw || newPw.length < 6) return toast.error('New password must be at least 6 characters');
        if (newPw !== confirmPw) return toast.error('Passwords do not match');
        setBusy(true);
        try {
            const res = await axios.post(`${API}/auth/forgot-password/verify`, {
                email_or_mobile: identifier.trim(),
                otp: otp.trim(),
                new_password: newPw,
            });
            toast.success(res.data.message || 'Password reset successfully');
            setStep('done');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'OTP verification failed');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={o => !o && close()}>
            <DialogContent className='max-w-md' data-testid='forgot-password-dialog'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        {step === 'done'
                            ? <><CheckCircle2 className='w-5 h-5 text-emerald-600' /> Password Reset</>
                            : <><KeyRound className='w-5 h-5 text-primary' /> Forgot Password</>}
                    </DialogTitle>
                </DialogHeader>

                {step === 'request' && (
                    <div className='space-y-3'>
                        <p className='text-xs text-muted-foreground'>Enter your registered email or 10-digit mobile number. We'll send an OTP to your mobile.</p>
                        <div className='space-y-1'>
                            <Label>Email or Mobile</Label>
                            <Input
                                value={identifier}
                                onChange={e => setIdentifier(e.target.value)}
                                placeholder='you@example.com or 9876543210'
                                className='min-h-[48px]'
                                data-testid='forgot-identifier'
                                autoFocus
                            />
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={close}>Cancel</Button>
                            <Button onClick={requestOtp} disabled={busy} data-testid='forgot-send-otp'>
                                {busy && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />} Send OTP
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {step === 'verify' && (
                    <div className='space-y-3'>
                        <div className='rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-2.5 flex items-start gap-2 text-xs'>
                            <ShieldCheck className='w-4 h-4 text-emerald-600 shrink-0 mt-0.5' />
                            <div>
                                OTP sent to:
                                {masked.mobile && <div>📱 WhatsApp/SMS: <b>+91 {masked.mobile}</b></div>}
                                {masked.email  && <div>✉️ Email: <b>{masked.email}</b></div>}
                                {!masked.mobile && !masked.email && <span> your registered contacts</span>}
                                <div className='opacity-70 mt-0.5'>Valid for 15 minutes</div>
                            </div>
                        </div>
                        <div className='space-y-1'>
                            <Label>6-digit OTP</Label>
                            <Input
                                inputMode='numeric'
                                value={otp}
                                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder='••••••'
                                className='min-h-[48px] tracking-widest text-center font-mono text-lg'
                                data-testid='forgot-otp'
                                autoFocus
                            />
                        </div>
                        <div className='space-y-1'>
                            <Label>New password</Label>
                            <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} placeholder='Min 6 characters' className='min-h-[48px]' data-testid='forgot-new-pw' />
                        </div>
                        <div className='space-y-1'>
                            <Label>Confirm new password</Label>
                            <PasswordInput value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className='min-h-[48px]' data-testid='forgot-confirm-pw' />
                        </div>
                        <DialogFooter className='flex-row justify-between gap-2'>
                            <Button variant='ghost' onClick={() => setStep('request')} data-testid='forgot-back'>Change number</Button>
                            <Button onClick={verifyOtp} disabled={busy} data-testid='forgot-reset-submit'>
                                {busy && <Loader2 className='w-3.5 h-3.5 mr-1 animate-spin' />} Reset password
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {step === 'done' && (
                    <div className='space-y-3 text-center py-2'>
                        <div className='w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center mx-auto'>
                            <CheckCircle2 className='w-9 h-9 text-emerald-600' />
                        </div>
                        <div className='font-semibold text-lg'>Password Reset Successful</div>
                        <p className='text-sm text-muted-foreground'>You can now login with your new password.</p>
                        <DialogFooter>
                            <Button className='w-full' onClick={close} data-testid='forgot-done-close'>Back to Login</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ForgotPasswordDialog;
